import type { WeatherCard } from '../../api/types'
import { beschreibe, himmelsrichtung, lageVon } from '../../lib/wetterlage'
import { WetterNennung, WetterZeichen } from '../map/Wetter'
import { useTexte } from '../../texte'

/**
 * The weather card in the chat (0.31.0): the current weather where the
 * traveller stood when tapping "Wetter" -- temperature and condition big,
 * place, region and country underneath, the values as a short list.
 *
 * **The values come from the server as Apple delivered them**; what is
 * missing is left out, not shown as zero. The words for the conditions are
 * in the texts (`karte.wetterlagen`), the sign in `lib/wetterlage.ts` -- the
 * same split as on the highlight. Apple Weather has to be named; the link
 * is the same one as under a highlight.
 *
 * The same card stands in the chat, in the book preview and, drawn by the
 * server, in PDF and EPUB (`WeatherCards::lines()`).
 */
export function Wetterkarte({ karte }: { karte: WeatherCard }) {
  const t = useTexte().wetterkarte
  const grad = karte.temperature_c === null ? null : Math.round(karte.temperature_c)
  const lage = karte.condition === null ? null : lageVon(karte.condition)
  const wort = karte.condition === null ? null : beschreibe(karte.condition)
  const ort = [karte.place, karte.region, karte.country].filter((s) => s !== '')

  const werte: string[] = []
  if (karte.feels_like_c !== null) werte.push(t.gefuehlt(Math.round(karte.feels_like_c)))
  if (karte.cloud_cover_pct !== null) werte.push(t.bewoelkung(karte.cloud_cover_pct))
  if (karte.precip_mm_h !== null && karte.precip_mm_h > 0) werte.push(t.niederschlag(karte.precip_mm_h))
  if (karte.wind_kmh !== null) {
    werte.push(t.wind(
      Math.round(karte.wind_kmh),
      karte.wind_dir_deg === null ? null : himmelsrichtung(karte.wind_dir_deg),
      karte.gust_kmh !== null && karte.gust_kmh > karte.wind_kmh ? Math.round(karte.gust_kmh) : null,
    ))
  }
  if (karte.humidity_pct !== null) werte.push(t.luftfeuchte(karte.humidity_pct))
  if (karte.uv_index !== null && karte.uv_index > 0) werte.push(t.uv(karte.uv_index))

  return (
    <div className="wetterkarte" data-wetterkarte={karte.id}>
      <p className="wetterkarte__kopf">
        <WetterZeichen lage={lage} size={36} />
        {grad !== null && <span className="wetterkarte__grad">{t.grad(grad)}</span>}
        {wort !== null && <span className="wetterkarte__lage">{wort}</span>}
      </p>
      {ort.length > 0 && <p className="wetterkarte__ort">{ort.join(t.trenner)}</p>}
      {werte.length > 0 && (
        <ul className="wetterkarte__werte">
          {werte.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      <p className="wetterkarte__fuss">
        <WetterNennung className="wetterkarte__quelle" />
      </p>
    </div>
  )
}
